/**
 * Surface system.
 *
 * A Surface is a UI primitive with a strict contract:
 *
 *   - id, version, class (transient | persistent | ambient)
 *   - persistence policy (ephemeral | session | longterm) — enforced by storage router
 *   - JSON Schema for the answer / state shape
 *   - render function (React component) — provided by the UI layer
 *   - telemetry hooks at the surface boundary
 *
 * Surfaces are registered at app boot and referenced by id throughout the
 * director, transport, and storage layers.
 */

import type { PersistenceClass } from '../storage/StorageRouter';
import type { Context } from '../identity';

export type SurfaceClass = 'transient' | 'persistent' | 'ambient';

export interface SurfaceDefinition<TState = unknown> {
  id: string;
  version: string;
  surfaceClass: SurfaceClass;
  persistence: PersistenceClass;
  /** Environment this surface belongs to. Used to gate by auth requirements. */
  environment: Context['environment'];
  /** JSON Schema describing surface state. */
  stateSchema: unknown;
  /** Default initial state. */
  initialState: () => TState;
  /** Lifecycle hooks — fired by the director. */
  hooks?: {
    onEnter?: (state: TState) => void;
    onExit?: (state: TState) => void;
    onAbandon?: (state: TState) => void;
  };
}

export class SurfaceRegistry {
  #surfaces = new Map<string, SurfaceDefinition>();

  register<T>(def: SurfaceDefinition<T>): void {
    if (this.#surfaces.has(def.id)) {
      throw new Error(`Surface ${def.id} already registered`);
    }
    this.#surfaces.set(def.id, def as SurfaceDefinition);
  }

  get(id: string): SurfaceDefinition | undefined {
    return this.#surfaces.get(id);
  }

  byEnvironment(env: Context['environment']): SurfaceDefinition[] {
    return [...this.#surfaces.values()].filter((s) => s.environment === env);
  }

  all(): SurfaceDefinition[] {
    return [...this.#surfaces.values()];
  }
}
